import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Dashboard from './Dashboard';
import PatientList from './PatientList';
import PatientDetails from './PatientDetails';
import Reports from './Reports';
import UnifiedPatientForm from './UnifiedPatientForm';
import AddVaccineForm from './AddVaccineForm';
import Header from './Header';
import MobileBottomNav from './MobileBottomNav';
import '../LoadingScreen.css';
import '../App.css';

export default function AuthenticatedApp() {
    const [currentView, setCurrentView] = useState('dashboard');
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [patients, setPatients] = useState([]);
    const [isAddPatientOpen, setIsAddPatientOpen] = useState(false);
    const [isAddVaccineOpen, setIsAddVaccineOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    const [editingPatient, setEditingPatient] = useState(null);
    const [editingVaccine, setEditingVaccine] = useState(null);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        // Sample data
        const samplePatients = [
            {
                id: '1',
                name: 'John Doe',
                mrn: 'MRN001234',
                phone: '(555) 123-4567',
                email: 'john.doe@email.com',
                age: 45,
                gender: 'Male',
                vaccines: [
                    { id: '1', name: 'Influenza', dose: 'Dose 1', date: '2024-01-15', status: 'completed' },
                    { id: '2', name: 'COVID-19', dose: 'Dose 2', date: '2024-02-01', status: 'completed' }
                ]
            },
            {
                id: '2',
                name: 'Jane Smith',
                mrn: 'MRN001235',
                phone: '(555) 234-5678',
                email: 'jane.smith@email.com',
                age: 32,
                gender: 'Female',
                vaccines: [
                    { id: '1', name: 'Influenza', dose: 'Dose 1', date: '2024-01-20', status: 'completed' },
                    { id: '2', name: 'HPV', dose: 'Dose 1', date: '2024-02-10', status: 'pending' }
                ]
            },
            {
                id: '3',
                name: 'Michael Johnson',
                mrn: 'MRN001236',
                phone: '(555) 345-6789',
                email: 'michael.j@email.com',
                age: 28,
                gender: 'Male',
                vaccines: [
                    { id: '1', name: 'COVID-19', dose: 'Dose 1', date: '2024-01-25', status: 'completed' }
                ]
            },
            {
                id: '4',
                name: 'Robert Brown',
                mrn: 'MRN001238',
                phone: '(555) 456-7890',
                email: 'robert.b@email.com',
                age: 52,
                gender: 'Male',
                vaccines: [
                    { id: '1', name: 'Influenza', dose: 'Dose 1', date: '2024-02-05', status: 'completed' }
                ]
            },
            {
                id: '5',
                name: 'Emily Williams',
                mrn: 'MRN001239',
                phone: '(555) 567-8901',
                email: 'emily.w@email.com',
                age: 29,
                gender: 'Female',
                vaccines: [
                    { id: '1', name: 'Influenza', dose: 'Dose 1', date: '2024-02-12', status: 'completed' },
                    { id: '2', name: 'HPV', dose: 'Dose 1', date: '2024-02-12', status: 'completed' }
                ]
            }
        ];

        const loadData = async () => {
            console.log('Loading patients data...');

            const startTime = Date.now();
            const minimumLoadTime = 2000;

            const savedPatients = localStorage.getItem('vaccinePatients');
            if (savedPatients && JSON.parse(savedPatients).length > 0) {
                setPatients(JSON.parse(savedPatients));
            } else {
                setPatients(samplePatients);
                localStorage.setItem('vaccinePatients', JSON.stringify(samplePatients));
            }

            const elapsed = Date.now() - startTime;
            if (elapsed < minimumLoadTime) {
                await new Promise(resolve =>
                    setTimeout(resolve, minimumLoadTime - elapsed)
                );
            }

            setLoading(false);
        };

        loadData();
    }, []);

    useEffect(() => {
        if (patients.length > 0) {
            localStorage.setItem('vaccinePatients', JSON.stringify(patients));
        }

        if (selectedPatient) {
            const updatedPatient = patients.find(p => p.id === selectedPatient.id);
            if (updatedPatient && updatedPatient !== selectedPatient) {
                setSelectedPatient(updatedPatient);
            }
        }
    }, [patients, selectedPatient]);

    const handleAddPatient = (patientData) => {
        const newPatient = {
            ...patientData,
            id: Date.now().toString(),
        };
        setPatients(prev => [newPatient, ...prev]);
    };

    const handleUpdatePatient = (updatedData) => {
        setPatients(prev => prev.map(p =>
            p.id === editingPatient.id ? { ...p, ...updatedData } : p
        ));
        setEditingPatient(null);
        setIsAddPatientOpen(false);
    };

    const handleDeletePatient = (patientId) => {
        if (window.confirm('Are you sure you want to delete this patient?')) {
            setPatients(prev => prev.filter(p => p.id !== patientId));
            if (selectedPatient && selectedPatient.id === patientId) {
                setSelectedPatient(null);
                setCurrentView('patients');
            }
        }
    };

    const handleEditClick = (patient) => {
        setEditingPatient(patient);
        setIsAddPatientOpen(true);
    };

    const handleAddVaccine = (patientId, vaccineData) => {
        setPatients(prev => prev.map(patient =>
            patient.id === patientId
                ? { ...patient, vaccines: [...patient.vaccines, vaccineData] }
                : patient
        ));
    };

    const handleUpdateVaccine = (patientId, vaccineData) => {
        setPatients(prev => prev.map(patient =>
            patient.id === patientId
                ? {
                    ...patient,
                    vaccines: patient.vaccines.map(v =>
                        v.id === vaccineData.id ? vaccineData : v
                    )
                }
                : patient
        ));
        setEditingVaccine(null);
        setIsAddVaccineOpen(false);
    };

    const handleAddNote = (patientId, noteText) => {
        const newNote = {
            id: Date.now().toString(),
            text: noteText,
            date: new Date().toISOString(),
            author: 'Dr. Current User'
        };

        setPatients(prev => prev.map(patient =>
            patient.id === patientId
                ? { ...patient, notes: [newNote, ...(patient.notes || [])] }
                : patient
        ));
    };

    const handleEditVaccineClick = (vaccine) => {
        setEditingVaccine(vaccine);
        setIsAddVaccineOpen(true);
    };

    const handleViewPatient = (patient) => {
        setSelectedPatient(patient);
        setCurrentView('details');
    };

    const handleBackToList = () => {
        setSelectedPatient(null);
        setCurrentView('patients');
    };

    const handleAddVaccineClick = () => {
        setIsAddVaccineOpen(true);
    };

    if (loading) {
        return (
            <div className="loading-screen">
                <motion.div
                    className="loading-container"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                >
                    <motion.div
                        className="loading-logo"
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{
                            type: "spring",
                            stiffness: 260,
                            damping: 20,
                            duration: 1
                        }}
                    >
                        <motion.div
                            className="logo-icon"
                            animate={{
                                rotate: 360,
                            }}
                            transition={{
                                rotate: {
                                    duration: 2,
                                    repeat: Infinity,
                                    ease: "linear"
                                }
                            }}
                        >
                            💉
                        </motion.div>
                    </motion.div>

                    <motion.div
                        className="loading-text"
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.5, duration: 0.5 }}
                    >
                        <h2>VaccineTracker</h2>
                        <p>Preparing your dashboard...</p>
                    </motion.div>

                    <motion.div
                        className="loading-progress-container"
                        initial={{ width: 0 }}
                        animate={{ width: "100%" }}
                        transition={{ delay: 0.8, duration: 2, ease: "easeInOut" }}
                    >
                        <div className="loading-progress-bar">
                            <motion.div
                                className="progress-fill"
                                initial={{ scaleX: 0 }}
                                animate={{ scaleX: 1 }}
                                transition={{
                                    delay: 1,
                                    duration: 1.5,
                                    ease: "easeInOut"
                                }}
                            />
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="App">
            <Header
                currentView={currentView}
                onNavigate={setCurrentView}
                onBack={currentView === 'details' ? handleBackToList : null}
                onAddPatient={() => setIsAddPatientOpen(true)}
            />

            <main className="main-content">
                <AnimatePresence mode="wait">
                    {currentView === 'dashboard' && (
                        <motion.div
                            key="dashboard"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            <Dashboard
                                patients={patients}
                                onViewPatient={handleViewPatient}
                                onNavigate={setCurrentView}
                            />
                        </motion.div>
                    )}

                    {currentView === 'patients' && (
                        <motion.div
                            key="patients"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            <PatientList
                                patients={patients}
                                onViewPatient={handleViewPatient}
                                onEditPatient={handleEditClick}
                                onDeletePatient={handleDeletePatient}
                            />
                        </motion.div>
                    )}

                    {currentView === 'details' && (
                        <motion.div
                            key="details"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            <PatientDetails
                                patient={selectedPatient}
                                onBack={handleBackToList}
                                onAddVaccine={handleAddVaccineClick}
                                onEdit={handleEditClick}
                                onDelete={handleDeletePatient}
                                onEditVaccine={handleEditVaccineClick}
                                onAddNote={handleAddNote}
                            />
                        </motion.div>
                    )}

                    {currentView === 'reports' && (
                        <motion.div
                            key="reports"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            <Reports patients={patients} />
                        </motion.div>
                    )}
                </AnimatePresence>
            </main>

            {isMobile && (
                <MobileBottomNav
                    currentView={currentView}
                    onNavigate={setCurrentView}
                    onAddPatient={() => setIsAddPatientOpen(true)}
                />
            )}

            <UnifiedPatientForm
                isOpen={isAddPatientOpen}
                onClose={() => {
                    setIsAddPatientOpen(false);
                    setEditingPatient(null);
                }}
                onAddPatient={handleAddPatient}
                onUpdatePatient={handleUpdatePatient}
                patient={editingPatient}
            />

            <AddVaccineForm
                isOpen={isAddVaccineOpen}
                onClose={() => {
                    setIsAddVaccineOpen(false);
                    setEditingVaccine(null);
                }}
                onAddVaccine={handleAddVaccine}
                onUpdateVaccine={handleUpdateVaccine}
                patient={selectedPatient}
                vaccine={editingVaccine}
            />
        </div>
    );
}
